/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'
import ChevronRightIcon from '@offerzen/zenicon/ChevronRightIcon'
import ChevronLeftIcon from '@offerzen/zenicon/ChevronLeftIcon'
import { colors } from '../../themes'
import { specialKeyPressed } from '../../helpers/eventHelpers'
import AccessibleIcon from '../AccessibleIcon'
import styles, { pageTheme } from './styles'

const maxVisibleItems = 7

const renderArrow = (arrow, currentPage, totalPages, onPageSelect) => {
  if (arrow === 'left') {
    return (
      <li
        onClick={() => {
          if (currentPage > 1) onPageSelect(currentPage - 1)
        }}
        onKeyDown={ev => {
          if (ev.which === 13 && !specialKeyPressed(ev) && currentPage > 1) onPageSelect(currentPage - 1)
        }}>
        <span className={cx(styles.page, currentPage <= 1 && styles.pageHidden)} tabIndex="0">
          <AccessibleIcon icon={ChevronLeftIcon} size={16} color={colors.jumbo} />
        </span>
      </li>
    )
  }
  if (arrow === 'right') {
    return (
      <li
        onClick={() => {
          if (currentPage < totalPages) onPageSelect(currentPage + 1)
        }}
        onKeyDown={ev => {
          if (ev.which === 13 && !specialKeyPressed(ev) && currentPage < totalPages) onPageSelect(currentPage + 1)
        }}>
        <span className={cx(styles.page, currentPage >= totalPages && styles.pageHidden)} tabIndex="0">
          <AccessibleIcon icon={ChevronRightIcon} size={16} color={colors.jumbo} />
        </span>
      </li>
    )
  }
  return null
}

const renderEllipsis = () => (
  <li>
    <span className={styles.page}>...</span>
  </li>
)

const renderPage = (theme, currentPage, page, onPageSelect) => (
  <li
    key={page}
    onClick={() => onPageSelect(page)}
    onKeyDown={ev => {
      if (ev.which === 13 && !specialKeyPressed(ev)) onPageSelect(page)
    }}>
    <span className={cx(styles.page, currentPage === page && pageTheme(theme))} tabIndex={currentPage === page ? '-1' : '0'}>
      {page}
    </span>
  </li>
)

const calculatePages = (currentPage, totalPages) => {
  // When there are less than 7 pages (the max amount of items visible), render all items without ellipsis
  // starting at 2 and up to last page - 1
  if (totalPages <= maxVisibleItems) {
    const pages = new Array(totalPages - 2).fill(0)
    return pages.map((_, index) => index + 2)
  }

  // When the current page is between 1 and 4, render all pages up to 5
  if (currentPage <= 4) return [2, 3, 4, 5]

  // When the current page is last page - 3 and last page, render all pages from last page - 4 up to last page
  if (currentPage >= totalPages - 3) return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1]

  // When the current page is somewhere in the middle, render only the pages before the current, the current, and after the current only
  return [currentPage - 1, currentPage, currentPage + 1]
}

const Pagination = ({ theme, className, currentPage, totalPages, onPageSelect }) => (
  <ul className={cx(styles.wrapper, className)}>
    {renderArrow('left', currentPage, totalPages, onPageSelect)}

    {renderPage(theme, currentPage, 1, onPageSelect)}

    {Boolean(currentPage > 4 && totalPages > maxVisibleItems) && <>{renderEllipsis()}</>}

    {calculatePages(currentPage, totalPages).map(page => renderPage(theme, currentPage, page, onPageSelect))}

    {Boolean(currentPage < totalPages - 3 && totalPages > maxVisibleItems) && <>{renderEllipsis()}</>}

    {renderPage(theme, currentPage, totalPages, onPageSelect)}

    {renderArrow('right', currentPage, totalPages, onPageSelect)}
  </ul>
)

Pagination.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'dark', 'tertiary', 'info']),
  className: PropTypes.string,
  onPageSelect: PropTypes.func.isRequired,
}

Pagination.defaultProps = {
  theme: 'primary',
}

export default Pagination
